/**
 * Presentation rules for the admin user feed, which mixes app events with records
 * read from the domain tables. Pure so the mapping to admin pages is pinned by
 * tests rather than found broken by a click.
 */
import type {
	AdminAppEventRow,
	AdminUserFeedRow,
	AdminUserRecordLink,
	AdminUserRecordRow,
} from '@orbit/api-types'

const RESOURCE_PATHS: Record<AdminUserRecordLink['resource'], string> = {
	trip: 'trips',
	event: 'events',
	idea: 'ideas',
	expense: 'expenses',
	itineraryImport: 'itinerary-imports',
}

/** The admin detail page a record row opens. */
export const recordPath = (link: AdminUserRecordLink): string =>
	`/admin/${RESOURCE_PATHS[link.resource]}/${link.id}`

/** A record row's one-line description: what it names, then its trip ("Blue Lagoon · Iceland"). */
export const recordSummary = (row: Pick<AdminUserRecordRow, 'summary' | 'trip'>): string =>
	[row.summary, row.trip?.name].filter(Boolean).join(' · ')

/** A key unique across both kinds of row: an app event and a record can share an id. */
export const feedRowKey = (row: AdminUserFeedRow): string => `${row.source}:${row.id}`

/**
 * The admin page an app event opens, when its payload names a record that has one.
 * A committed itinerary import is recorded only as an app event (the record row
 * steps aside for it), so its `importId` is the way to the import.
 */
export const appEventLink = (row: Pick<AdminAppEventRow, 'eventType' | 'data'>): AdminUserRecordLink | null => {
	const importId = row.data?.importId
	return row.eventType === 'itinerary_imported' && typeof importId === 'string'
		? { resource: 'itineraryImport', id: importId }
		: null
}
